import React, { useEffect, useMemo, useRef, useState } from 'react';
import AdminHeader from '../components/AdminHeader';
import { Link } from 'react-router-dom';
import Beads from '../components/Beads';
import AdminModal from './AdminModal';
import { ReactComponent as EditSVG } from '../../../assets/images/edit.svg';
import { useDashboardQuery } from '../apis/dashboard';
import { useExplainMutation } from '../apis/explain';
import { useAssignUserToGroupMutation } from '../apis/groups';
import {
  compareParentsByGroupAndName,
  getParentGroupIds,
  getParentGroupNames,
  hasParentGroup,
  isParentInGroup,
} from '../apis/normalizeDashboardData';
import { hasFullFacilitatorProgramAccessRole } from '../../../utils/authRole';

//make props for SelectGroupPopup component
// groupList: array of groups
// user: the user object for whom the group is being edited
// currentGroup: the currently selected group ID
// handleClose: function to close the modal
// isOpen: boolean indicating if the modal is open

const SelectGroupPopup = ({
  groupList,
  parent,
  handleClose,
  isOpen,
  canCreateGroup,
}) => {
  const [selectedGroup, setSelectedGroup] = useState(
    getParentGroupIds(parent).length === 1 ? getParentGroupIds(parent)[0] : '',
  );
  const [errorMessage, setErrorMessage] = useState('');
  const assignUserToGroupMutation = useAssignUserToGroupMutation({
    onSuccess: () => {
      setErrorMessage('');
      handleClose();
    },
  });

  if (!parent) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedGroup) {
      setErrorMessage('Select a group before saving changes.');
      return;
    }

    try {
      await assignUserToGroupMutation.mutateAsync({
        groupId: selectedGroup,
        userId: parent.userId,
      });
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to update the parent group.');
    }
  };

  return (
    <AdminModal
      title={
        <>
          Edit Group for{' '}
          <span className='text-purple underline'>
            {parent.firstName} {parent.lastName}
          </span>
        </>
      }
      isOpen={isOpen}
      handleClose={() => {
        setErrorMessage('');
        handleClose();
      }}
    >
      {groupList.length === 0 ? (
        <div className='flex flex-col gap-4'>
          <p className='max-w-[46ch] text-sm leading-relaxed text-gray-700'>
            {canCreateGroup
              ? 'No groups have been created yet. Create a group first, then you can assign this participant to it.'
              : 'No groups have been created yet. An administrator will need to create a group before this participant can be assigned to one.'}
          </p>
          {canCreateGroup ? (
            <Link
              to='/facilitator/dashboard/create-group'
              onClick={handleClose}
              className='inline-flex w-fit items-center justify-center rounded bg-purple px-4 py-2 text-sm font-bold text-white no-underline transition-colors duration-200 hover:bg-purple-800'
            >
              Create A New Group
            </Link>
          ) : null}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className='flex flex-col gap-4'>
            {errorMessage ? (
              <p className='rounded border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-700'>
                {errorMessage}
              </p>
            ) : null}
            {groupList.map((group) => (
              <label
                key={group.groupId}
                className='flex items-start gap-3 rounded border border-gray-200 bg-white p-3 text-sm'
              >
                <input
                  type='radio'
                  name='group-selection'
                  value={group.groupId}
                  checked={selectedGroup === group.groupId}
                  onChange={() => {
                    setSelectedGroup(group.groupId);
                    setErrorMessage('');
                  }}
                  className='mt-1 accent-blue-600'
                />
                <span>
                  <span className='block font-bold text-dark-blue'>
                    {group.groupName}
                  </span>
                  <span className='block text-xs text-gray-700'>
                    Parents:{' '}
                    <span className='font-bold'>{group.parentCount}</span>
                  </span>
                  <span className='block text-xs text-gray-700'>
                    Facilitators:{' '}
                    <span className='font-bold'>
                      {group.facilitators
                        .map((item) => `${item.firstName} ${item.lastName}`)
                        .join(', ')}
                    </span>
                  </span>
                </span>
              </label>
            ))}
            <button
              type='submit'
              className='mt-2 rounded bg-purple px-4 py-2 text-sm font-bold text-white transition-colors duration-200 hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-70'
              disabled={assignUserToGroupMutation.isLoading}
            >
              {assignUserToGroupMutation.isLoading
                ? 'Saving...'
                : 'Save Changes'}
            </button>
          </div>
        </form>
      )}
    </AdminModal>
  );
};

const getNotFinishedFacilitators = (facilitators) => {
  return facilitators.filter(
    (facilitator) =>
      !facilitator.completedProgram &&
      Boolean(facilitator.firstName) &&
      Boolean(facilitator.lastName),
  );
};

const getUnnasignedFacilitators = (facilitators) => {
  return facilitators.filter(
    (facilitator) => !facilitator.firstName || !facilitator.lastName,
  );
};

const hasProgramCompletionDate = (facilitator) => {
  if (
    !facilitator.programCompleted ||
    typeof facilitator.programCompleted === 'boolean'
  ) {
    return false;
  }

  return !Number.isNaN(new Date(facilitator.programCompleted).getTime());
};

const getCompletedFacilitators = (facilitators) =>
  facilitators.filter(
    (facilitator) =>
      hasProgramCompletionDate(facilitator) &&
      Boolean(facilitator.firstName) &&
      Boolean(facilitator.lastName),
  );

const formatProgramCompletionDate = (date) =>
  new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

const getFacilitatorLoginLabel = (facilitator) =>
  facilitator.username ?? facilitator.userName ?? facilitator.userId;

const FascilitatorHome = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');
  const [showWelcomeModal, setShowWelcomeModal] = useState(
    () => localStorage.getItem('showExplain') === 'true',
  );
  const hasAcknowledgedExplanation = useRef(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const canCreateGroup = hasFullFacilitatorProgramAccessRole(
    localStorage.getItem('role'),
  );
  const dashboardLabel = canCreateGroup
    ? 'Admin ezDashboard'
    : 'Facilitator ezDashboard';
  const dashboardName = localStorage.getItem('name')?.trim() || 'there';
  const { data: normalizedData, isLoading, isError } = useDashboardQuery();
  const { mutate: acknowledgeExplanation } = useExplainMutation();

  useEffect(() => {
    if (
      showWelcomeModal &&
      !isLoading &&
      !isError &&
      !hasAcknowledgedExplanation.current
    ) {
      hasAcknowledgedExplanation.current = true;
      localStorage.setItem('showExplain', 'false');
      acknowledgeExplanation({});
    }
  }, [acknowledgeExplanation, isError, isLoading, showWelcomeModal]);
  const filteredParents = useMemo(
    () =>
      (normalizedData?.parents ?? [])
        .filter((parent) => {
          const nameMatches = `${parent.firstName} ${parent.lastName}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
          const groupMatches =
            selectedGroupFilter === 'all' ||
            (selectedGroupFilter === 'no-group' && !hasParentGroup(parent)) ||
            isParentInGroup(parent, selectedGroupFilter);
          return nameMatches && groupMatches;
        })
        .sort((firstParent, secondParent) => {
          if (selectedGroupFilter === 'all') {
            return compareParentsByGroupAndName(firstParent, secondParent);
          }

          return `${firstParent.firstName} ${firstParent.lastName}`.localeCompare(
            `${secondParent.firstName} ${secondParent.lastName}`,
          );
        }),
    [normalizedData?.parents, searchTerm, selectedGroupFilter],
  );
  const unassignedFacilitators = useMemo(
    () => getUnnasignedFacilitators(normalizedData?.facilitators ?? []),
    [normalizedData?.facilitators],
  );
  const unfinishedFacilitators = useMemo(
    () => getNotFinishedFacilitators(normalizedData?.facilitators ?? []),
    [normalizedData?.facilitators],
  );
  const completedFacilitators = useMemo(
    () => getCompletedFacilitators(normalizedData?.facilitators ?? []),
    [normalizedData?.facilitators],
  );
  const showFacilitatorStatus =
    canCreateGroup &&
    (unassignedFacilitators.length > 0 ||
      unfinishedFacilitators.length > 0 ||
      completedFacilitators.length > 0);

  if (isLoading) {
    return <div>Loading...</div>;
  }
  if (isError) {
    return <div>Error loading data.</div>;
  }

  // const getGroupForUser = (userId) => {
  //   return data.groups.find((g) => g.memberIds.includes(userId));
  // };

  return (
    <div className='min-h-screen bg-[#eef6fa] text-left'>
      <AdminHeader>
        <Link
          to='/facilitator/dashboard/data'
          className='inline-flex min-h-[42px] min-w-[120px] items-center justify-center rounded border-2 border-light-blue bg-white px-4 py-2 text-base font-semibold leading-tight text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
        >
          Export Data
        </Link>
      </AdminHeader>
      <main className='mx-auto max-w-5xl p-4'>
        <div className='my-6'>
          <h1 className='text-3xl font-bold text-dark-blue'>
            {dashboardLabel}
          </h1>
          <Link
            className='mt-2 inline-flex rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
            to='/facilitator/home'
          >
            <i>ez</i>Training Homepage
          </Link>
        </div>

        <div className='relative'>
          {canCreateGroup && selectedParent && (
            <SelectGroupPopup
              groupList={normalizedData.groups}
              parent={selectedParent}
              handleClose={() => setSelectedParent(null)}
              isOpen={!!selectedParent}
              canCreateGroup={canCreateGroup}
            />
          )}
          <AdminModal
            title={`Welcome to the ${dashboardLabel}!`}
            isOpen={showWelcomeModal}
            handleClose={() => setShowWelcomeModal(false)}
          >
            <div className='space-y-4 text-base leading-relaxed'>
              <p>
                Parents within your organization will be associated by a
                organization ID.
              </p>
              <p>
                On this page, you can add new groups and assign parents to
                groups.
              </p>
              <p>
                You can review module completion at a glance on the homepage or
                click on a participant for more detailed info.
              </p>
              <p>
                Use filters to view participants across the org, in specific
                groups, or individually.
              </p>
              <p>
                By clicking <strong>Export Data</strong>, you can download a CSV
                file of all usage data.
              </p>
            </div>
          </AdminModal>
          <div className='my-6 rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm '>
            <div
              className='mb-3 text-2xl font-bold text-dark-blue
            border-b border-gray-200 pb-2
            '
            >
              Welcome back!
            </div>
            <span className='text-sm font-bold uppercase tracking-wide text-purple'>
              Org ID
            </span>
            <div className='text-xl font-bold text-dark-blue'>
              {normalizedData.orgId}
            </div>
            {showFacilitatorStatus ? (
              <div className='mt-4 border-t border-gray-200 pt-4'>
                <h2 className='text-base font-bold text-dark-blue'>
                  Facilitator Status
                </h2>
                {unassignedFacilitators.length ? (
                  <>
                    <p className='mt-2 max-w-[70ch] text-sm leading-relaxed text-gray-700'>
                      * These are facilitator usernames that are still
                      unregistered. Facilitators are registered once they sign
                      in with their username and password.
                    </p>
                    <div className='mt-3 flex flex-wrap gap-2'>
                      {unassignedFacilitators.map((facilitator) => (
                        <span
                          key={facilitator.userId}
                          className='rounded-full border border-gray-300 bg-white px-3 py-1 text-sm font-semibold text-dark-blue'
                        >
                          {getFacilitatorLoginLabel(facilitator)}
                        </span>
                      ))}
                    </div>
                  </>
                ) : null}
                {unfinishedFacilitators.length ? (
                  <>
                    <p className='mt-4 max-w-[70ch] text-sm leading-relaxed text-gray-700'>
                      * These are facilitators who have not completed
                      <i>ez</i>Training.
                    </p>
                    <div className='mt-3 flex flex-wrap gap-2'>
                      {unfinishedFacilitators.map((facilitator) => (
                        <span
                          key={facilitator.userId}
                          className='rounded-full border border-gray-300 bg-white px-3 py-1 text-sm font-semibold text-dark-blue'
                        >
                          {facilitator.firstName} {facilitator.lastName}
                        </span>
                      ))}
                    </div>
                  </>
                ) : null}
                {completedFacilitators.length ? (
                  <div className='mt-5 border-t border-gray-200 pt-4'>
                    <h3 className='text-sm font-bold text-dark-blue'>
                      Facilitators who have completed <i>ez</i>Training
                    </h3>
                    <div className='mt-3 flex gap-2 overflow-x-auto pb-2'>
                      {completedFacilitators.map((facilitator) => (
                        <div
                          key={facilitator.userId}
                          className='min-w-[220px] shrink-0 rounded border border-gray-300 bg-white px-3 py-2'
                        >
                          <p className='text-sm font-semibold text-dark-blue'>
                            {facilitator.firstName} {facilitator.lastName}
                          </p>
                          <p className='mt-1 text-xs text-gray-600'>
                            Completed:{' '}
                            {formatProgramCompletionDate(
                              facilitator.programCompleted,
                            )}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          {canCreateGroup ? (
            <div className='my-6 flex flex-wrap items-start gap-3'>
              <Link
                to='/facilitator/dashboard/create-group'
                className='inline-flex min-h-[44px] w-[200px] items-center justify-center whitespace-nowrap rounded bg-purple px-5 py-2.5 font-sans text-base font-semibold leading-tight text-white no-underline transition-colors duration-200 hover:bg-purple-800'
              >
                Create A New Group
              </Link>
            </div>
          ) : null}

          {normalizedData.groups.length > 0 && (
            <p className='max-w-[75ch] text-lg text-gray-700 mt-10 font-semibold'>
              Click on the Group button to see detailed group information or
              edit a group.
            </p>
          )}

          <div className='my-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
            {normalizedData.groups.map((group) => (
              <Link
                to={`/facilitator/dashboard/group/${group.groupId}`}
                key={group.groupId}
                className='rounded border border-light-blue bg-white px-4 py-3 text-sm no-underline shadow-sm transition-colors duration-200 hover:bg-blue-50'
              >
                <span className='mb-2 block text-base font-bold text-light-blue'>
                  {group.groupName}
                </span>
                <span className='block text-gray-700'>
                  Parents:{' '}
                  <span className='font-bold'>{group.parentCount}</span>
                </span>
                <span className='block text-gray-700'>
                  Facilitators:{' '}
                  {group.facilitators.map((facilitator, index) => (
                    <span key={facilitator.userId} className='font-bold'>
                      {index > 0 ? ', ' : ''}
                      {facilitator.firstName} {facilitator.lastName}
                    </span>
                  ))}
                </span>
              </Link>
            ))}
          </div>

          <section className='my-8 rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
              <h2 className='text-xl font-bold text-dark-blue'>
                Parent Module Completion
              </h2>
              <div className='flex flex-wrap items-center gap-3'>
                <input
                  type='text'
                  placeholder='Search by name...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className='rounded border border-gray-300 px-3 py-2 text-sm'
                />
                <select
                  value={selectedGroupFilter}
                  onChange={(e) => setSelectedGroupFilter(e.target.value)}
                  className='rounded border border-gray-300 px-3 py-2 text-sm'
                >
                  <option value='all'>All Groups</option>
                  {normalizedData.groups.map((g) => (
                    <option key={g.groupId} value={g.groupId}>
                      {g.groupName}
                    </option>
                  ))}
                  <option value='no-group'>Group Unassigned</option>
                </select>
              </div>
            </div>

            <div className='grid grid-cols-[22%_22%_56%] gap-4 rounded-t border border-gray-200 bg-white px-3 py-2.5 text-sm font-bold text-dark-blue'>
              <div>Parent Name</div>
              <div>Group</div>
              <div>
                <div>Module Completion</div>
                <div className='mt-1 text-sm font-normal text-gray-500'>
                  Hover over numbers to see the parent’s engagement index for
                  that module. A yellow circle means the module is completed,
                  but the{' '}
                  <span className='font-semibold text-purple'>
                    engagement index{' '}
                  </span>{' '}
                  is below 60%.
                </div>
              </div>
            </div>

            {filteredParents.map((parent) => {
              return (
                <div
                  key={parent.userId}
                  className='grid grid-cols-[22%_22%_56%] gap-4 border-x border-b border-gray-200 bg-white px-3 py-3 text-sm items-start'
                >
                  <div>
                    <Link
                      className='font-bold text-blue-600 hover:underline'
                      to={`/users/${parent.userId}`}
                    >
                      {parent.firstName} {parent.lastName}
                    </Link>
                  </div>
                  <div className='flex items-center gap-2'>
                    {getParentGroupNames(parent).length ? (
                      getParentGroupNames(parent).join(', ')
                    ) : (
                      <span className='text-red-600 font-bold'>
                        Group Unassigned
                      </span>
                    )}
                    {canCreateGroup ? (
                      <EditSVG
                        className='cursor-pointer w-5'
                        onClick={() => setSelectedParent(parent)}
                      />
                    ) : null}
                  </div>
                  <div className='overflow-x-auto pb-1'>
                    <Beads
                      statusArr={parent.modules}
                      uniqueId={parent.userId}
                    />
                  </div>
                </div>
              );
            })}
          </section>
        </div>
      </main>
    </div>
  );
};

export default FascilitatorHome;
